import React, {FC} from 'react';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {newPalette, NewPaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsGraphMetricsEnum,
  AnalyticsGraphMetricValue,
  AnalyticsNumericDataPoint,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {helpCenterUrl} from '../../../../store/stateless/navigationActions';
import {
  AnalyticsReportsEnum,
  BasicReportProps,
  ContributingSegmentsOptionsEnum,
  isComputedGraphMetric,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/analyticsReportHelpers';
import {ChartSerie} from '../../charts/commons/types';
import {EmptyStateRenderer} from '../../reportElements/charts/emptyState';
import {NoRuleEmptyState} from '../../reportElements/charts/noRuleEmptyState';
import {ReportLineChart} from '../../reportElements/charts/reportLineChart';
import {AnyGraphMetricsEnum} from '../../reportElements/helperTypes';
import {ReportHeroCards} from '../../reportElements/heroCards/reportHeroCards';
import {ReportTable} from '../../reportElements/table/reportTable';
import {teamColumnId} from '../../reportElements/table/tableHelpers';

/*
 * Constants.
 */

enum SlaPerformanceGraphSerieTypesEnum {
  WARNINGS = 'warnings',
  BREACHES = 'breaches',
}

export interface SlaPerformanceGraphSeries {
  label: string;
  type: AnalyticsDataPointTypesEnum;
  start: number;
  values: {
    [SlaPerformanceGraphSerieTypesEnum.WARNINGS]: AnalyticsNumericDataPoint;
    [SlaPerformanceGraphSerieTypesEnum.BREACHES]: AnalyticsNumericDataPoint;
  };
}

const perfGraphSeries: Array<ChartSerie<AnalyticsGraphMetricValue>> = [
  {
    accessor: (d) => d.value,
    label: '', // Label will be set dynamically based on the selected metric.
    color: newPalette[NewPaletteColorsEnum.GREEN].regular,
  },
];

/*
 * Props.
 */

interface SlaReportReportProps extends BasicReportProps {
  isTeamColumnVisible: boolean;
}

/*
 * Component.
 */

export const SlaReport: FC<SlaReportReportProps> = ({isLoading, isTeamColumnVisible}) => {
  const dispatch = useAppDispatch();

  const onDataPointSelect = (
    dataPoint: AnalyticsGraphMetricValue,
    serie: ChartSerie<AnalyticsGraphMetricValue>,
    selectedMetric?: AnyGraphMetricsEnum,
  ) => {
    // Adding a type guard but it shouldn't technically happen.
    if (!selectedMetric || isComputedGraphMetric(selectedMetric)) {
      return;
    }

    dispatch(
      setMetricDetailsAction(selectedMetric, {
        [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
          [ContributingSegmentsOptionsEnum.ROW]: dataPoint.start,
        },
      }),
    );
  };

  const {
    slaHeroCardsReportId,
    slaReportTableId,
    slaTableDefaultColumns,
    slaTableMetric,
    slaPerfOverTimeSectionId,
    slaPerfGraphMertics,
    slaDisplayTeamColumn,
  } = computeReportConfig(isTeamColumnVisible);

  return (
    <>
      {/* Metrics Cards */}
      <ReportHeroCards
        reportType={AnalyticsReportsEnum.SLA}
        reportSectionId={slaHeroCardsReportId}
        isLoading={isLoading}
      />
      {/* SLA performance graph */}
      <ReportLineChart
        title={<Trans id="analytics-sla-report-performance-title">SLA Performance Over Time</Trans>}
        reportSectionId={slaPerfOverTimeSectionId}
        isLoading={isLoading}
        availableMetrics={slaPerfGraphMertics}
        series={perfGraphSeries}
        xAccessor={(d) => d.label}
        renderEmptyState={slaEmptyStateRenderer}
        onDataPointSelect={onDataPointSelect}
      />
      {/* SLA status table */}
      <ReportTable
        reportSectionId={slaReportTableId}
        isLoading={isLoading}
        metric={slaTableMetric}
        defaultColumnIds={slaTableDefaultColumns}
        renderEmptyState={slaEmptyStateRenderer}
        blockedColumnIds={slaDisplayTeamColumn ? undefined : [teamColumnId]}
        disableSelection
      />
    </>
  );
};

/*
 * Helpers.
 */

const slaEmptyStateRenderer: EmptyStateRenderer = ({width, height}) => (
  <NoRuleEmptyState
    width={width}
    height={height}
    title={<Trans id="analytics-empty-state-sla-title">No SLA rule set up</Trans>}
    buttonText={<Trans id="analytics-sla-empty-state-no-rule-button">Set up rule</Trans>}
    learnMoreLink={`${helpCenterUrl}/t/q6hh1bw/sla-rules`}
  />
);

function computeReportConfig(displayTeamColumn: boolean) {
  return {
    slaHeroCardsReportId: ReportElementIdsEnum.SLA_METRICS_CARDS,
    slaReportTableId: ReportElementIdsEnum.SLA_STATUS_TABLE_V2,
    slaTableDefaultColumns: ['sla_rule', 'sla_time_goal', 'sla_breaches', 'avg_sla_breach_time'],
    slaTableMetric: AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2,
    slaPerfOverTimeSectionId: ReportElementIdsEnum.SLA_PERF_GRAPH_V2,
    slaPerfGraphMertics: [
      AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH,
      AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH,
      AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH,
    ],
    slaDisplayTeamColumn: displayTeamColumn,
  };
}
